import {z} from 'zod';

/**
 * OS LIMITES DO PARECER, e por que eles moram num arquivo só deles.
 *
 * Estes números eram do parecer (`editorial-review.ts`), e o contrato da
 * correção (`GenerationRevisionSchema`, em `generation.ts`) tinha uma cópia
 * escrita à mão dos mesmos campos com outro teto. Foi assim que o achado F07 da
 * auditoria de 2026-09-06 nasceu: o parecer aceitava mil caracteres de prosa, a
 * correção aceitava trezentos, e um parecer VÁLIDO de 400 caracteres derrubava a
 * correção no schema ANTES de a chamada ao escritor sair. A execução local da
 * auditoria mostrou exatamente isso: o mesmo achado passou como parecer e foi
 * recusado como pedido de correção.
 *
 * Um `import` do parecer dentro de `generation.ts` fecharia um ciclo: o parecer
 * já importa `GenerationUsageSchema` e os tipos do conteúdo daquele arquivo.
 * Ciclo de módulo em ESM não é erro de compilação, é uma ordem de avaliação que
 * funciona até o dia em que alguém troca a ordem do `index.ts` e um `const` vira
 * `undefined` em execução. Por isso os limites descem para cá, e os dois lados
 * importam DAQUI.
 *
 * A regra que este arquivo carrega: **o achado atravessa a esteira inteiro**.
 * Cortar o texto do achado para caber no pedido da correção seria pior que
 * recusar, e não se faz em lugar nenhum daqui: um achado cortado no meio de uma
 * frase continua parecendo um achado, e ninguém descobre que a segunda metade
 * sumiu.
 */

// ---------------------------------------------------------------------------
// Os dois tetos de texto, e por que eles são DIFERENTES entre si
// ---------------------------------------------------------------------------

/**
 * O teto de uma CITAÇÃO: o trecho da peça, ou o texto lido dentro da imagem.
 *
 * Continua curto porque é CÓPIA de um pedaço da peça, e não redação do crítico.
 * Um trecho citado acima disto deixaria de ser trecho: seria a peça inteira
 * devolvida como prova de si mesma.
 */
export const EDITORIAL_EXCERPT_MAX_CHARS = 300;

/**
 * O teto da PROSA do crítico, e o defeito medido que o subiu.
 *
 * Medido em produção em 2026-09-06, com a chave real, pelo ensaio
 * `parecer-editorial.js --ensaio`: nas DUAS peças ensaiadas (a foto de notícia e
 * a capa do Reel) o modelo respondeu e o schema de casa recusou o parecer
 * INTEIRO, por `mainChange` numa e por `findings.1.issue` na outra, as duas com
 * a mesma mensagem (`Too big: expected string to have <=300 characters`). Com
 * `DEVMIL_EDITORIAL_MEDIA_REVIEW=parecer`, cada peça pagaria uma chamada com
 * imagem e nenhum parecer visual seria gravado. É por isso que o interruptor
 * ficou desligado em produção até esta correção.
 *
 * A causa não era o modelo: o teto de trezentos NUNCA foi dito a ele. Nem o
 * pedido de texto nem o visual mencionavam tamanho de campo nenhum. As duas
 * pontas foram corrigidas, e a folga entre elas é deliberada: o pedido cobra
 * frases curtas (`EDITORIAL_ASKED_MAX_CHARS`) e o teto de casa fica ACIMA do que
 * se pediu, porque descartar um parecer já pago por trinta caracteres de prosa é
 * jogar fora a medição inteira para defender um número que ninguém defende.
 *
 * O teto continua existindo, e não virou texto livre: ele é a cerca contra a
 * resposta gigante de um modelo que se perdeu, e contra instrução plantada
 * chegando em volume. Mil caracteres cabem num parágrafo e não cabem num ataque.
 *
 * Truncar seria pior que recusar, e não se faz em lugar nenhum daqui: um parecer
 * cortado no meio de uma frase continua parecendo um parecer, e ninguém descobre
 * que a segunda metade do achado sumiu.
 */
export const EDITORIAL_PROSE_MAX_CHARS = 1_000;

/**
 * O tamanho que o PEDIDO cobra do crítico, e por que ele é MENOR que o teto.
 *
 * São dois números de propósito: este é o que se pede, aquele é o que se aceita.
 * A folga entre os dois é o que faz um crítico levemente verboso continuar
 * produzindo parecer, em vez de produzir uma linha de log.
 *
 * Ele mora aqui, e não no adaptador, porque os DOIS pedidos (o de texto e o
 * visual) o cobram, e dois números escritos à mão divergiriam no dia em que
 * alguém ajustasse um deles.
 */
export const EDITORIAL_ASKED_MAX_CHARS = 280;

// ---------------------------------------------------------------------------
// O ACHADO, que atravessa a esteira inteira
// ---------------------------------------------------------------------------

/**
 * Onde o achado mora.
 *
 * Lista de PERMISSÃO, e ela espelha os campos que a peça publica. `overall` é
 * para o que não cabe em campo nenhum ("a peça inteira repete a de terça"), e
 * existe para o revisor não ser obrigado a pendurar um achado de conjunto num
 * campo qualquer só para caber no enum.
 */
export const EDITORIAL_FINDING_FIELDS = [
  'hook',
  'caption',
  'cta',
  'screen_text',
  'claim',
  'overall',
] as const;
export const EditorialFindingFieldSchema = z.enum(EDITORIAL_FINDING_FIELDS);
export type EditorialFindingField = z.infer<typeof EditorialFindingFieldSchema>;

/**
 * Um achado do crítico de TEXTO, do jeito que ele nasce e do jeito que ele
 * chega ao escritor.
 *
 * UM schema, e não dois iguais. `EditorialReviewSchema` valida o que o revisor
 * devolveu; `GenerationRevisionSchema` valida o que a esteira manda ao escritor.
 * As duas coisas carregam o MESMO achado, e enquanto foram dois schemas escritos
 * à mão elas divergiram em silêncio até um parecer de 400 caracteres ser aceito
 * de um lado e recusado do outro.
 */
export const EditorialFindingSchema = z.strictObject({
  field: EditorialFindingFieldSchema,
  excerpt: z.string().trim().min(1).max(EDITORIAL_EXCERPT_MAX_CHARS).nullable(),
  issue: z.string().trim().min(1).max(EDITORIAL_PROSE_MAX_CHARS),
});
export type EditorialFinding = z.infer<typeof EditorialFindingSchema>;

// ---------------------------------------------------------------------------
// A PENDÊNCIA e os DOIS VEREDITOS, que também atravessam a esteira inteira
// ---------------------------------------------------------------------------
//
// Eles nasceram no parecer (`editorial-review.ts`) e desceram para cá em
// 2026-09-07, pelo mesmo caminho do achado e pelo mesmo motivo: o pedido de
// CORREÇÃO (`GenerationRevisionSchema`, em `generation.ts`) passou a carregá-los,
// e um `import` do parecer dentro de `generation.ts` fecharia o ciclo de módulo
// descrito no cabeçalho deste arquivo.
//
// ## O defeito medido que os trouxe, em 2026-09-07
//
// Treze pareceres de texto em produção, todos da mesma versão de pedido
// (`devmil-editorial-p05-2026-09-c`). Nenhum passou no piso. As notas NÃO eram o
// problema: cinco dos treze já alcançavam a soma de 16. O que reprovou os treze
// foi o mesmo veto, pendência factual crítica, em 13 de 13.
//
// E a pendência ficava no parecer: a correção mandada ao escritor levava achado,
// mudança principal e módulos, e mais nada. O crítico identificava a pendência, a
// pendência derrubava a peça, e o escritor nunca era informado dela. A reescrita
// era estruturalmente incapaz de consertar a única coisa que reprovava todas as
// peças, e com o laço de correção isso vira reescrita paga contra um problema
// invisível.
//
// Os outros dois vetos da régua tinham o MESMO buraco, e por isso vieram juntos:
// `SOURCE_OVERSTATED` (3 dos 13) sai de `sourceSupport.status === 'overstated'` e
// `REPEATED_TOPIC_AND_STRUCTURE` (5 dos 13) sai de `repetition.status === 'both'`.
// Os dois derrubam a peça independentemente da nota, e nenhum dos dois chegava a
// quem tinha que consertar.

/**
 * A pendência, e por que a gravidade é campo e não adjetivo.
 *
 * O piso da régua exige "sem pendência factual crítica". Se a gravidade viesse
 * escrita dentro do texto, decidir se a pendência era crítica seria ler prosa de
 * modelo, e a régua deixaria de ser verificável. Com o campo, `critical` derruba
 * o piso mesmo numa peça de 20 sobre 20, e isso é uma linha de código que alguém
 * consegue conferir.
 *
 * A gravidade viaja ao escritor junto com o texto, e não é enfeite lá também:
 * crítica é o motivo pelo qual a peça foi reprovada e precisa sair da reescrita
 * resolvida; menor é um ajuste que ninguém reprovou.
 */
export const EditorialPendencySchema = z.strictObject({
  severity: z.enum(['critical', 'minor']),
  text: z.string().trim().min(1).max(EDITORIAL_PROSE_MAX_CHARS),
});
export type EditorialPendency = z.infer<typeof EditorialPendencySchema>;

/**
 * A REPETIÇÃO contra o histórico, como veredito e não como prosa.
 *
 * Até a fase 1 a repetição existia só como achado de texto livre: o revisor
 * escrevia "isto repete a peça de terça" num `finding`, e a régua não tinha como
 * agir sobre aquilo. Ler prosa de modelo para decidir se a peça repete é o mesmo
 * defeito que a gravidade da pendência resolveu virando campo.
 *
 * Os quatro estados são separados porque significam coisas diferentes:
 *
 * - `no_history`: não havia peça recente para comparar. É informação, e não
 *   "não repete": o revisor precisa poder dizer que não teve como olhar;
 * - `none`: havia histórico e a peça não repete;
 * - `topic`: fala do mesmo assunto de uma peça recente;
 * - `structure`: usa a mesma forma (mesmo tipo de abertura, mesma sequência);
 * - `both`: as duas coisas, que é a peça da semana passada com outra roupa.
 *
 * `both` derruba o piso, e o motivo está em `avaliarParecer`, no domínio.
 */
export const EDITORIAL_REPETITION_STATUS = [
  'no_history',
  'none',
  'topic',
  'structure',
  'both',
] as const;
export const EditorialRepetitionSchema = z.strictObject({
  status: z.enum(EDITORIAL_REPETITION_STATUS),
  /** Qual peça recente. Nulo quando não há nenhuma para apontar. */
  reference: z.string().trim().min(1).max(EDITORIAL_PROSE_MAX_CHARS).nullable(),
  note: z.string().trim().min(1).max(EDITORIAL_PROSE_MAX_CHARS).nullable(),
});
export type EditorialRepetition = z.infer<typeof EditorialRepetitionSchema>;

/**
 * O GANCHO contra a FONTE: ele promete mais do que a prova sustenta?
 *
 * O dono escreveu a regra em uma frase: "nota alta não compensa informação sem
 * prova". Para que essa frase seja cerca e não conselho, ela precisa de um campo
 * que a régua leia, e é este. `overstated` derruba o piso mesmo numa peça de 20
 * sobre 20, exatamente como a pendência crítica.
 *
 * `no_sources` é honesto e não é desculpa: uma peça de opinião sem prova nenhuma
 * não tem fonte para exagerar. Quem cobra prova para afirmação com número é o
 * portão de afirmações, e ele continua rodando depois disto.
 */
export const EDITORIAL_SOURCE_SUPPORT_STATUS = [
  'no_sources',
  'supported',
  'partially_supported',
  'overstated',
] as const;
export const EditorialSourceSupportSchema = z.strictObject({
  status: z.enum(EDITORIAL_SOURCE_SUPPORT_STATUS),
  /** O trecho do gancho que vai além da fonte. Nulo quando não há um. */
  excerpt: z.string().trim().min(1).max(EDITORIAL_EXCERPT_MAX_CHARS).nullable(),
  note: z.string().trim().min(1).max(EDITORIAL_PROSE_MAX_CHARS).nullable(),
});
export type EditorialSourceSupport = z.infer<
  typeof EditorialSourceSupportSchema
>;
